import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ButtonPattern from '@mlb/react-patterns/components/Button/button.tsx';
import { IconWrapper } from '@mlb/react-patterns/components/Icon/icon.style.tsx';
import Popover from '@mlb/react-patterns/components/Popover/popover.tsx';
import { AnimateFrom } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.types.ts';
import { Label } from '@mlb/react-patterns/components/Button/button.style.tsx';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { useMedia } from 'bdvp-common/src/hooks';
import { getIsTouchDevice } from 'bdvp-common/src/utils';
import { useDictionary } from 'bdvp-common/src/hooks/config';
import ModalContainer from 'bdvp-common/src/containers/Modal';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';
import { Tracking } from 'bdvp-tracking';

import Content from './Content';

const { MENU_MODAL_NAME } = modalConstants;
const ANIMATION_DURATION = 0.3;

const Modal = styled(ModalContainer)`
    @supports (padding-top: constant(safe-area-inset-top)) {
        --safe-area-inset-top: constant(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: constant(safe-area-inset-bottom, 0);
    }

    @supports (padding-top: env(safe-area-inset-top)) {
        --safe-area-inset-top: env(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: env(safe-area-inset-bottom, 0);
    }
`;

const Button = styled(ButtonPattern).attrs({
    size: 'lg',
    iconName: 'grid',
    styleName: 'default',
    shape: 'hollow-borderless',
})`
    {
        margin-left: 5px;
        font-size: 15px;
        white-space: nowrap;

        ${Label} {
            display: none;
        }

        ${media.lgmin`
            margin-left: 0px;

            ${Label} {
                display: inline-block;
            }
        `}
        ${IconWrapper} {
            stroke: none;
            padding-top: 0;
        }
    }
`;

const Menu = ({
    subnav,
}) => {

    const {
        channels_text: menuText = 'Channels',
    } = useDictionary();
    const isMobile = useMedia([`(min-width: ${BREAKPOINTS.md})`], [false], true);

    const [
        showContainer,
        setShowContainer,
    ] = useState(false);

    const [
        isClient,
        setIsClient,
    ] = useState(false);

    useEffect(() => {

        setIsClient(true);

    }, []);

    const closeContainer = () => {

        setShowContainer(false);

    };

    const content = (
        <Content
            subnav={subnav}
            onClick={closeContainer}
        />
    );

    const isTouchDevice = isClient && getIsTouchDevice();

    const button = (
        <Button
            label={menuText}
            disabled={!isClient || (isMobile && showContainer)}
            active={showContainer}
            handlers= {{
                onClick: (event) => {

                    if(showContainer) {

                        return;

                    }

                    event.preventDefault();

                    const handleMenuButtonClick = () => {

                        Tracking.trackMenuButtonClick();
                        setShowContainer(true);

                    };

                    if(isMobile) {

                        setTimeout(handleMenuButtonClick, ANIMATION_DURATION * 1000);

                    } else {

                        handleMenuButtonClick();

                    }

                },
            }}
            hasHover={!isTouchDevice}
        />
    );

    if(!isClient) {

        return button;

    }

    if(isMobile) {

        return (
            <>
                {button}
                <Modal
                    name={MENU_MODAL_NAME}
                    isOpen={showContainer}
                    headerTitle={menuText}
                    width={'100%'}
                    height={'99%'}
                    overlay={true}
                    animate={true}
                    animateFrom={AnimateFrom.BOTTOM}
                    animationDuration={ANIMATION_DURATION}
                    position={{
                        bottom: 0,
                        left: 0,
                    }}
                    handlers={
                        {
                            onClosed: closeContainer,
                        }
                    }
                >
                    {content}
                </Modal>
            </>
        );

    }

    return (
        <Popover
            isOpen={showContainer}
            placement={'bottom'}
            showOnEvent={'click'}
            popoverOffset={[
                0,
                2,
            ]}
            preventOverflowPadding={20}
            popoverContent={content}
            onClose={closeContainer}
        >
            {button}
        </Popover>
    );

};

Menu.propTypes = {
    subnav: PropTypes.array,
};

Menu.defaultProps = {
    subnav: [],
};

export default Menu;
